import '../styles/Contact.css';
import { useRef, useState } from 'react';
import emailjs from 'emailjs-com';
import ReCAPTCHA from 'react-google-recaptcha';
import { ContactIcon } from './Icons';

const createInitialForm = () => ({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  zip: '',
  description: '',
  time: new Date().toLocaleString(),
});

export default function Contact() {
  const [form, setForm] = useState(createInitialForm);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [submission, setSubmission] = useState({ state: 'idle', message: '' });
  const recaptchaRef = useRef();
  const isSubmitting = submission.state === 'submitting';

  const handleChange = (event) => {
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
    if (submission.state !== 'idle') setSubmission({ state: 'idle', message: '' });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!captchaValue) {
      setSubmission({ state: 'error', message: 'Please complete the reCAPTCHA before submitting.' });
      return;
    }

    if (!privacyAccepted) {
      setSubmission({ state: 'error', message: 'Please confirm that we may contact you about this request.' });
      return;
    }

    setSubmission({ state: 'submitting', message: 'Sending your estimate request…' });

    try {
      await emailjs.send('service_hrzgk3d', 'template_4xkx3vl', form, '4b2hx0U2fBPx4Z0rP');
      setForm(createInitialForm());
      setCaptchaValue(null);
      setPrivacyAccepted(false);
      recaptchaRef.current?.reset();
      setSubmission({
        state: 'success',
        message: 'Thank you. Your estimate request was sent, and our team will be in touch.',
      });
    } catch (error) {
      console.error('Email error:', error);
      setSubmission({
        state: 'error',
        message: 'We could not send your request. Please try again or call (408) 969-0882.',
      });
    }
  };

  return (
    <section id="Estimate-Container" className="Estimate-Section">
      <div className="Estimate-Container">
        <div id="Estimate-Start" className="Estimate-Heading">
          <p className="Section-Eyebrow Section-Eyebrow--light">LET’S GET STARTED</p>
          <h2 className="Estimate-Title">Get Your Free Estimate</h2>
          <p className="Estimate-Subtitle">
            Tell us about your garage door needs and our team will be in touch to discuss the right solution for your home.
          </p>
        </div>

        <div className="Estimate-Content">
          <form className="Estimate-Form" aria-label="Request a free estimate" onSubmit={handleSubmit}>
            <p className="Estimate-Kicker">PROJECT DETAILS</p>
            <h3 className="Estimate-Form-Title">Request an estimate</h3>
            <div className="Estimate-Form-Row">
              <label>
                <span>First name</span>
                <input autoComplete="given-name" type="text" name="firstName" placeholder="Jane" value={form.firstName} onChange={handleChange} required />
              </label>
              <label>
                <span>Last name</span>
                <input autoComplete="family-name" type="text" name="lastName" placeholder="Smith" value={form.lastName} onChange={handleChange} required />
              </label>
            </div>
            <label>
              <span>Email address</span>
              <input autoComplete="email" type="email" name="email" placeholder="jane@example.com" value={form.email} onChange={handleChange} required />
            </label>
            <div className="Estimate-Form-Row">
              <label>
                <span>Phone number</span>
                <input autoComplete="tel" inputMode="tel" type="tel" name="phone" placeholder="(408) 555-0123" value={form.phone} onChange={handleChange} required />
              </label>
              <label>
                <span>ZIP code</span>
                <input autoComplete="postal-code" inputMode="numeric" pattern="[0-9]{5}(-[0-9]{4})?" type="text" name="zip" placeholder="95050" value={form.zip} onChange={handleChange} required />
              </label>
            </div>
            <label>
              <span>How can we help?</span>
              <textarea name="description" placeholder="Tell us about your door, project, or repair needs…" value={form.description} onChange={handleChange} rows={4} required />
            </label>
            <div className="Estimate-Captcha">
              <ReCAPTCHA ref={recaptchaRef} sitekey="6LdgyMcrAAAAAPUyzsP9ZxmUnpQ6HNZGtxgJCNus" onChange={setCaptchaValue} />
            </div>
            <label className="Estimate-Consent">
              <input
                type="checkbox"
                checked={privacyAccepted}
                onChange={(event) => setPrivacyAccepted(event.target.checked)}
                required
              />
              <span>I agree that Golden State Garage Doors may contact me about this estimate request.</span>
            </label>

            {submission.message && (
              <div
                className={`Estimate-Status is-${submission.state}`}
                role={submission.state === 'error' ? 'alert' : 'status'}
                aria-live="polite"
              >
                {submission.message}
              </div>
            )}

            <button type="submit" className="Estimate-Button" disabled={isSubmitting}>
              <span>{isSubmitting ? 'Sending request…' : 'Get free estimate'}</span>
              <span aria-hidden="true">→</span>
            </button>
            <p className="Estimate-Privacy">Your information stays private and is only used to respond to your request.</p>
          </form>

          <aside className="Estimate-Sidebar" aria-label="Contact details">
            <div className="Estimate-SidebarIntro">
              <p className="Estimate-Kicker">CONTACT DETAILS</p>
              <h3>Prefer to talk?</h3>
              <p>Reach our local team directly during business hours.</p>
            </div>
            <a className="Estimate-ContactBlock" href="tel:4089690882">
              <span className="Estimate-Icon"><ContactIcon type="phone" /></span>
              <span>
                <span className="Estimate-ContactTitle">Call us</span>
                <span>(408) 969-0882</span>
              </span>
            </a>
            <a className="Estimate-ContactBlock" href="mailto:gsgd@sbcglobal.net">
              <span className="Estimate-Icon"><ContactIcon type="email" /></span>
              <span>
                <span className="Estimate-ContactTitle">Email us</span>
                <span>gsgd@sbcglobal.net</span>
              </span>
            </a>
            <div className="Estimate-ContactBlock">
              <span className="Estimate-Icon"><ContactIcon type="location" /></span>
              <span>
                <span className="Estimate-ContactTitle">Service area</span>
                <span>Greater Bay Area</span>
                <span className="Estimate-ContactNote">California</span>
              </span>
            </div>
            <div className="Estimate-ContactBlock">
              <span className="Estimate-Icon"><ContactIcon type="hours" /></span>
              <span>
                <span className="Estimate-ContactTitle">Business hours</span>
                <span>Monday–Friday, 8am–5pm</span>
                <span className="Estimate-ContactNote">Closed weekends</span>
              </span>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
